((root, factory) => {
  const api = factory(typeof module === 'object' && module.exports ? require('./dim-context-continuation.js') : root.DIMContextContinuation);
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.DIMPaidOperation = api;
})(typeof window !== 'undefined' ? window : globalThis, continuation => {
  'use strict';

  const SCHEMA = 'dim-paid-operation@1';
  const PATHS = new Set(['/v1/prompt', '/v1/image', '/v1/image/stream']);
  const CONTEXT_FIELDS = ['schemaVersion', 'storefrontId', 'storefrontRevision', 'bindingId', 'mode', 'purpose', 'catalogueRevision', 'activationEpoch', 'receipt'];
  const RUNTIME_FIELDS = ['environment', 'instanceId', 'themeId', 'workerOrigin'];

  const failure = (code, message = code) => {
    const error = new Error(message);
    error.code = code;
    return error;
  };
  const copy = value => JSON.parse(JSON.stringify(value));
  const equal = (left, right) => JSON.stringify(left) === JSON.stringify(right);
  const exactKeys = (value, fields) => value && typeof value === 'object' && !Array.isArray(value)
    && Object.keys(value).length === fields.length && fields.every(field => Object.prototype.hasOwnProperty.call(value, field));
  const text = (value, maximum = 512) => typeof value === 'string' && value.length > 0 && value.length <= maximum;
  const validRuntime = value => exactKeys(value, RUNTIME_FIELDS) && ['development', 'production'].includes(value.environment)
    && typeof value.instanceId === 'string' && /^[A-Za-z0-9_.-]{1,100}$/.test(value.instanceId)
    && typeof value.themeId === 'string' && /^[1-9][0-9]{0,29}$/.test(value.themeId)
    && typeof value.workerOrigin === 'string' && value.workerOrigin.length <= 2048
    && (() => { try { const url = new URL(value.workerOrigin); return url.origin === value.workerOrigin && url.protocol === 'https:'; } catch { return false; } })();
  const validContext = value => {
    try { return Boolean(continuation.validateContext(value)); } catch (_) { return false; }
  };
  // A fresh server proof may rotate its receipt without changing the operation's
  // authority. Stored record ownership still compares the exact original record.
  const sameScope = (left, right) => equal(left.runtime, right.runtime) && continuation.sameAuthorityIdentity(left.context, right.context);
  const validStaticContext = value => value && typeof value === 'object' && ['storefrontId', 'bindingId', 'mode', 'purpose'].every(field => text(value[field], 120))
    && ((['primary', 'alias', 'campaign'].includes(value.mode) && value.purpose === 'customer') || (value.mode === 'preview' && value.purpose === 'staff-preview'));

  function create({ runtime, storage, fetchImpl, crypto: cryptoImpl, now = Date.now } = {}) {
    if (!runtime || typeof runtime.verify !== 'function' || !runtime.runtime || !storage
      || typeof storage.getItem !== 'function' || typeof storage.setItem !== 'function' || typeof storage.removeItem !== 'function') {
      throw failure('PAID_OPERATION_UNCONFIGURED');
    }
    const requester = typeof runtime.authenticatedFetch === 'function' ? runtime.authenticatedFetch.bind(runtime) : fetchImpl;
    if (typeof requester !== 'function') throw failure('PAID_OPERATION_UNCONFIGURED');
    const cryptography = cryptoImpl || globalThis.crypto;
    if (!cryptography?.subtle?.digest || typeof TextEncoder !== 'function') throw failure('PAID_OPERATION_UNCONFIGURED');
    const staticContext = runtime.context;
    if (!validRuntime(runtime.runtime) || !validStaticContext(staticContext)) throw failure('PAID_OPERATION_UNCONFIGURED');
    // Development keys include the endpoint and theme. The changing attestation
    // fields remain in the record and are compared before any mutating request.
    const prefix = runtime.runtime.environment === 'development'
      ? `dim:dev:${runtime.runtime.instanceId}:theme:${runtime.runtime.themeId}:backend:${encodeURIComponent(runtime.runtime.workerOrigin)}`
      : `dim:backend:${encodeURIComponent(runtime.runtime.workerOrigin)}`;
    const RECORD_KEY = `${prefix}:paid-operation:v1:${encodeURIComponent(staticContext.storefrontId)}:${encodeURIComponent(staticContext.bindingId)}:${encodeURIComponent(staticContext.mode)}:${encodeURIComponent(staticContext.purpose)}`;
    let preparing = false;

    const read = () => {
      try {
        const raw = storage.getItem(RECORD_KEY);
        if (raw === null) return null;
        const record = JSON.parse(raw);
        if (!exactKeys(record, ['schemaVersion', 'path', 'requestToken', 'bodyDigest', 'scope', 'issuedAt', 'expiresAt'])
          || record.schemaVersion !== SCHEMA || !text(record.requestToken, 4096)
          || typeof record.bodyDigest !== 'string' || !/^[a-f0-9]{64}$/.test(record.bodyDigest)
          || !PATHS.has(record.path) || !exactKeys(record.scope, ['runtime', 'context']) || !validRuntime(record.scope.runtime)
          || !validContext(record.scope.context) || !Number.isSafeInteger(record.issuedAt) || record.issuedAt < 0
          || (record.expiresAt !== null && (!Number.isSafeInteger(record.expiresAt) || record.expiresAt < record.issuedAt))) throw failure('PAID_OPERATION_RECORD_INVALID');
        return record;
      } catch (error) {
        if (error?.code) throw error;
        throw failure('PAID_OPERATION_STORAGE_UNAVAILABLE');
      }
    };
    const write = record => {
      try { storage.setItem(RECORD_KEY, JSON.stringify(record)); }
      catch { throw failure('PAID_OPERATION_STORAGE_UNAVAILABLE'); }
    };
    const removeIfCurrent = record => {
      let current;
      try { current = read(); }
      catch { return false; }
      if (!current || current.requestToken !== record.requestToken || current.bodyDigest !== record.bodyDigest || !equal(current.scope, record.scope)) return false;
      try { storage.removeItem(RECORD_KEY); return true; }
      catch { throw failure('PAID_OPERATION_STORAGE_UNAVAILABLE'); }
    };
    const digest = async bodyJson => {
      const bytes = new TextEncoder().encode(bodyJson);
      const hash = new Uint8Array(await cryptography.subtle.digest('SHA-256', bytes));
      return Array.from(hash, byte => byte.toString(16).padStart(2, '0')).join('');
    };
    const verifyScope = async () => {
      let verified;
      try { verified = await runtime.verify({ force: true }); }
      catch (error) { throw error?.code ? error : failure('PAID_OPERATION_RUNTIME_UNVERIFIED'); }
      const context = verified || runtime.context;
      const runtimeScope = runtime.runtime;
      if (!validRuntime(runtimeScope) || !validContext(context)) throw failure('PAID_OPERATION_RUNTIME_UNVERIFIED');
      return Object.freeze({ runtime: Object.fromEntries(RUNTIME_FIELDS.map(field => [field, runtimeScope[field]])), context: Object.fromEntries(CONTEXT_FIELDS.map(field => [field, context[field]])) });
    };
    const responseJson = async response => {
      try { return await response.clone().json(); }
      catch { return null; }
    };
    const headersFor = token => Object.freeze({ 'Content-Type': 'application/json', 'X-DIM-Request-Token': token });
    const terminal = status => status === 'not_dispatched' || status === 'rejected' || status === 'completed' || status === 'completed_unavailable';
    const poll = async (record, { signal } = {}) => {
      const currentScope = await verifyScope();
      if (!sameScope(currentScope, record.scope)) throw failure('PAID_OPERATION_CONTEXT_CHANGED');
      let response;
      try { response = await requester(`${record.path}/status`, { method: 'GET', headers: { 'X-DIM-Request-Token': record.requestToken }, signal }); }
      catch { throw failure('PAID_OPERATION_STATUS_UNCERTAIN'); }
      const result = await responseJson(response);
      if (typeof result?.status === 'string') {
        if (terminal(result.status)) removeIfCurrent(record);
        return Object.freeze({ status: result.status, requestToken: record.requestToken, explicitRetryRequired: result.explicitRetryRequired === true });
      }
      throw failure(result?.code || 'PAID_OPERATION_STATUS_UNCERTAIN');
    };

    async function prepare({ path, body, signal, generationAction } = {}) {
      if (!PATHS.has(path)) throw failure('PAID_OPERATION_INVALID_PATH');
      if (!body || typeof body !== 'object' || Array.isArray(body)) throw failure('PAID_OPERATION_INVALID_BODY');
      if (generationAction !== undefined && (!['/v1/image', '/v1/image/stream'].includes(path)
        || !generationAction || Object.keys(generationAction).sort().join(',') !== 'createdAt,id'
        || typeof generationAction.id !== 'string' || !/^[A-Za-z0-9_-]{8,120}$/.test(generationAction.id)
        || !Number.isSafeInteger(generationAction.createdAt) || generationAction.createdAt < 0)) throw failure('PAID_OPERATION_INVALID_GENERATION_ACTION');
      if (preparing) throw failure('PAID_OPERATION_IN_PROGRESS');
      preparing = true;
      try {
        const scope = await verifyScope();
        const existing = read();
        if (existing) {
          if (!sameScope(existing.scope, scope)) throw failure('PAID_OPERATION_CONTEXT_CHANGED');
          if (existing.expiresAt === null || now() < existing.expiresAt) throw failure('PAID_OPERATION_PENDING');
          const state = await poll(existing, { signal });
          if (!terminal(state.status)) throw failure('PAID_OPERATION_PENDING');
        }
        let bodyJson;
        try { bodyJson = JSON.stringify(body); }
        catch { throw failure('PAID_OPERATION_INVALID_BODY'); }
        if (typeof bodyJson !== 'string') throw failure('PAID_OPERATION_INVALID_BODY');
        const bodyDigest = await digest(bodyJson);
        let issue;
        try {
          const response = await requester(`${path}/issue`, { method: 'POST', headers: { 'Content-Type': 'application/json',
            ...(generationAction ? { 'X-DIM-Generation-Id': generationAction.id, 'X-DIM-Generation-Created-At': String(generationAction.createdAt) } : {}) }, body: bodyJson, signal });
          issue = await responseJson(response);
          if (!response.ok) throw failure(issue?.code || 'PAID_OPERATION_ISSUE_FAILED');
        } catch (error) {
          throw error?.code ? error : failure('PAID_OPERATION_ISSUE_UNCERTAIN');
        }
        if (issue?.status !== 'issued' || typeof issue.requestToken !== 'string' || !issue.requestToken) throw failure('PAID_OPERATION_ISSUE_INVALID');
        if (generationAction && (!exactKeys(issue.generationAction, ['id', 'createdAt'])
          || issue.generationAction.id !== generationAction.id
          || issue.generationAction.createdAt !== generationAction.createdAt)) {
          throw failure('GENERATION_ACTION_UNAVAILABLE', 'This selected idea cannot be generated until the image service is updated. Your draft is saved.');
        }
        const currentScope = await verifyScope();
        if (!sameScope(scope, currentScope)) throw failure('PAID_OPERATION_CONTEXT_CHANGED');
        const record = Object.freeze({ schemaVersion: SCHEMA, path, requestToken: issue.requestToken, bodyDigest, scope, issuedAt: now(), expiresAt: Number.isSafeInteger(issue.expiresAt) ? issue.expiresAt : null });
        write(record); // The durable lock exists before a charge-capable execute request.
        let executing = false;
        let executionStarted = false;
        const assertCurrent = () => {
          const current = read();
          if (!current || current.requestToken !== record.requestToken || current.bodyDigest !== record.bodyDigest || !equal(current.scope, record.scope)) throw failure('PAID_OPERATION_SUPERSEDED');
        };
        return Object.freeze({
          requestToken: record.requestToken,
          executeHeaders: headersFor(record.requestToken),
          record: Object.freeze({ path: record.path, requestToken: record.requestToken, bodyDigest: record.bodyDigest, scope: copy(record.scope), issuedAt: record.issuedAt, expiresAt: record.expiresAt }),
          async execute({ signal: executeSignal } = {}) {
            if (executing || executionStarted) throw failure('PAID_OPERATION_EXECUTING');
            assertCurrent();
            executing = true;
            try {
              const currentScope = await verifyScope();
              if (!sameScope(currentScope, record.scope)) throw failure('PAID_OPERATION_CONTEXT_CHANGED');
              executionStarted = true;
              return await requester(path, { method: 'POST', headers: headersFor(record.requestToken), body: bodyJson, signal: executeSignal });
            } catch (error) {
              // A network/stream interruption cannot prove dispatch did not happen.
              throw error?.code ? error : failure('PAID_OPERATION_UNCERTAIN');
            } finally { executing = false; }
          },
          complete(metadata = {}) {
            if (!executionStarted || metadata?.status !== 'completed') throw failure('PAID_OPERATION_COMPLETION_UNCONFIRMED');
            return removeIfCurrent(record);
          },
          uncertain() { return false; },
          async status({ signal: statusSignal } = {}) {
            assertCurrent();
            return poll(record, { signal: statusSignal });
          }
        });
      } finally { preparing = false; }
    }

    return Object.freeze({
      prepare,
      pending: () => read() ? copy(read()) : null,
      status: options => {
        const record = read();
        if (!record) throw failure('PAID_OPERATION_NO_PENDING');
        return poll(record, options);
      }
    });
  }

  return Object.freeze({ create });
});
